import type { Buffer } from "./buffer";
import { createBuffer } from "./buffer";
import type { Cursor } from "./cursor";
import { clampCursor, createCursor } from "./cursor";
import type { History } from "./history";
import { EMPTY_HISTORY } from "./history";
import type { Keystroke } from "./keystroke";
import type { MacroRecording, Macros } from "./macros";
import { EMPTY_MACROS } from "./macros";
import type { Marks } from "./marks";
import { EMPTY_MARKS } from "./marks";
import type { Mode } from "./mode";
import type { PendingCommand } from "./pending-command";
import { EMPTY_PENDING } from "./pending-command";
import type { Registers } from "./registers";
import { EMPTY_REGISTERS } from "./registers";

export type SearchDirection = "forward" | "backward";

export interface SearchState {
  readonly query: string;
  readonly direction: SearchDirection;
}

export type FindCharVariant = "f" | "t";

export interface FindCharState {
  readonly variant: FindCharVariant;
  readonly char: string;
  readonly direction: SearchDirection;
}

export interface EditorState {
  readonly buffer: Buffer;
  readonly cursor: Cursor;
  readonly mode: Mode;
  readonly pending: PendingCommand;
  readonly registers: Registers;
  readonly history: History;
  readonly marks: Marks;
  readonly macros: Macros;
  /** 記録中のマクロ。記録していない間はnull。 */
  readonly macroRecording: MacroRecording | null;
  /** 直前の変更（バッファを変更したコマンド）を再現するための生キー列。ドットリピート用。 */
  readonly lastChange: readonly Keystroke[] | null;
  /** 挿入モード中に蓄積している生キー列。Escで確定してlastChangeへ移す。 */
  readonly insertSession: readonly Keystroke[] | null;
  /** コマンドラインモード（`/` `?` `:`）で入力中の文字列（先頭にプレフィックスを含む）。 */
  readonly commandLine: string | null;
  /** 直前の検索（`n` `N` で再利用）。 */
  readonly lastSearch: SearchState | null;
  /** 直前の行内検索（`;` `,` で再利用）。 */
  readonly lastFind: FindCharState | null;
  /** ビジュアルモードの起点。ノーマルモードでは null。 */
  readonly visualAnchor: Cursor | null;
  readonly statusMessage: string | null;
  readonly usedKeystrokes: number;
}

export function createEditorState(lines: readonly string[], cursor?: Cursor): EditorState {
  const buffer = createBuffer(lines);
  return {
    buffer,
    cursor: clampCursor(buffer, cursor ?? createCursor(0, 0)),
    mode: "normal",
    pending: EMPTY_PENDING,
    registers: EMPTY_REGISTERS,
    history: EMPTY_HISTORY,
    marks: EMPTY_MARKS,
    macros: EMPTY_MACROS,
    macroRecording: null,
    lastChange: null,
    insertSession: null,
    commandLine: null,
    lastSearch: null,
    lastFind: null,
    visualAnchor: null,
    statusMessage: null,
    usedKeystrokes: 0,
  };
}
